(() => {
  if (customElements.get('halloween-motion')) return;

  customElements.define('halloween-motion', class extends HTMLElement {
    connectedCallback() {
      this.teardown();
      this.controller = new AbortController();
      this.animations = new Set();
      this.revealAnimations = new WeakMap();
      this.seen = new WeakSet();
      this.root = this.closest('main') || document;
      this.page = this.root.querySelector('.halloween-sale-hero')?.parentElement;
      this.hero = this.root.querySelector('#halloween-top');
      this.hint = this.root.querySelector('.halloween-scroll-hint');
      if (!this.hint && this.hero) {
        const hintTemplate = this.querySelector('template[data-halloween-scroll-hint]');
        const hint = hintTemplate?.content.firstElementChild?.cloneNode(true);
        if (hint) {
          this.hero.append(hint);
          this.hint = hint;
          this.injectedHint = hint;
        }
      }
      this.links = [...this.querySelectorAll('a[href^="#"]')];
      this.chapters = this.links.map(link => this.root.querySelector(link.getAttribute('href'))).filter(Boolean);
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const signal = this.controller.signal;
      this.root.classList?.add('halloween-motion-active');

      this.addEventListener('click', event => {
        const link = event.target.closest('.halloween-motion-nav__link');
        if (link && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) this.goTo(link, event);
      }, { signal });
      this.hint?.addEventListener('click', event => {
        if (!event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) this.goTo(this.hint, event);
      }, { signal });
      this.root.addEventListener('focusin', event => {
        const target = event.target.closest('[data-halloween-reveal]');
        this.revealAnimations.get(target)?.finish();
      }, { signal });

      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.updateLocation();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true, signal });
      window.addEventListener('resize', this.onScroll, { passive: true, signal });
      this.root.addEventListener('halloween:countdown-visibility', this.onScroll, { signal });
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.stopMotion();
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopMotion();
      }, { signal });

      this.setupReveals();
      this.setupOilChanges();
      this.updateLocation();
    }

    animate(element, frames, options) {
      if (!element || this.motion.matches || document.hidden || !element.animate) return;
      const animation = element.animate(frames, options);
      this.animations.add(animation);
      animation.finished.then(() => this.animations.delete(animation), () => this.animations.delete(animation));
      return animation;
    }

    reveal(element, delay = 0) {
      if (this.seen.has(element) || element.hidden) return;
      this.seen.add(element);
      element.setAttribute('data-halloween-reveal', '');
      const animation = this.animate(element, [
        { opacity: .25, transform: `translateY(${window.innerWidth <= 760 ? 6 : 10}px)` },
        { opacity: 1, transform: 'translateY(0)' }
      ], { duration: 1100, delay, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'backwards' });
      if (animation) this.revealAnimations.set(element, animation);
    }

    breathe(element, delay = 0) {
      this.animate(element, [
        { boxShadow: '0 0 0 0 rgba(216,161,117,0)' },
        { boxShadow: '0 0 0 4px rgba(216,161,117,.18)', offset: .5 },
        { boxShadow: '0 0 0 0 rgba(216,161,117,0)' }
      ], { duration: 2400, delay, iterations: 2, easing: 'ease-in-out' });
    }

    setupReveals() {
      if (this.motion.matches || !('IntersectionObserver' in window)) return;
      const heroVisible = this.hero && this.hero.getBoundingClientRect().bottom > 0 && this.hero.getBoundingClientRect().top < window.innerHeight;
      if (heroVisible) {
        [...this.hero.querySelectorAll('.halloween-sale-hero__copy > *')].forEach((element, index) => this.reveal(element, Math.min(index * 120, 600)));
        this.breathe(this.hero.querySelector('.halloween-sale-hero__button--primary'), 1400);
        this.animate(this.hint?.querySelector('.icon'), [{ transform: 'translateY(0)' }, { transform: 'translateY(4px)', offset: .5 }, { transform: 'translateY(0)' }], { duration: 2200, iterations: 2, easing: 'ease-in-out' });
      }
      const groups = [
        ['.halloween-bundles__heading', 0], ['.halloween-bundle', 120],
        ['.halloween-diffusers__heading', 0], ['.halloween-diffuser-card', 100],
        ['.halloween-pumpkin__content', 0], ['.halloween-pumpkin__art', 0],
        ['.halloween-oils__heading', 0], ['.halloween-oil-card:not([hidden])', 110],
        ['.halloween-preorders__heading', 0], ['.halloween-preorder-card', 100],
        ['.halloween-final__inner > *', 120]
      ];
      this.revealObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const element = entry.target;
          this.reveal(element, Number(element.dataset.halloweenDelay || 0));
          if (element.matches('.halloween-final__actions')) this.breathe(element.querySelector('.halloween-final__button--primary'), 500);
          if (element.matches('.halloween-bundle') && element.querySelector('.halloween-bundle__tag')?.textContent.includes('MOST POPULAR')) this.breathe(element.querySelector('.halloween-bundle__price'), 600);
          this.revealObserver.unobserve(element);
        });
      }, { threshold: .08, rootMargin: '0px 0px -28px 0px' });
      groups.forEach(([selector, stagger]) => {
        this.root.querySelectorAll(selector).forEach((element, index) => {
          element.dataset.halloweenDelay = String(Math.min(index * stagger, 450));
          this.revealObserver.observe(element);
        });
      });
    }

    setupOilChanges() {
      const grid = this.root.querySelector('.halloween-oils__grid');
      if (!grid || !('MutationObserver' in window)) return;
      this.oilObserver = new MutationObserver(records => {
        if (this.motion.matches || !records.some(record => record.attributeName === 'hidden')) return;
        const rect = grid.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;
        grid.querySelectorAll('[data-oil-moving]:not([hidden])').forEach((card, index) => {
          this.seen.add(card);
          this.revealObserver?.unobserve(card);
          this.animate(card, [{ opacity: .35, transform: 'translateX(7px)' }, { opacity: 1, transform: 'translateX(0)' }], { duration: 550, delay: index * 60, easing: 'ease-in-out', fill: 'backwards' });
        });
      });
      this.oilObserver.observe(grid, { subtree: true, attributes: true, attributeFilter: ['hidden'] });
    }

    goTo(link, event) {
      const target = this.root.querySelector(link.getAttribute('href'));
      if (!target) return;
      event.preventDefault();
      // Existing section scroll margins include the sticky header and countdown.
      target.scrollIntoView({ behavior: this.motion.matches ? 'instant' : 'smooth', block: 'start' });
      const heading = target.querySelector('h2');
      if (heading) {
        heading.tabIndex = -1;
        heading.focus({ preventScroll: true });
      }
      const url = new URL(window.location.href);
      url.hash = target.id;
      history.replaceState(history.state, '', url);
    }

    updateLocation() {
      const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 75;
      const countdown = this.root.querySelector('.halloween-sale-countdown')?.getBoundingClientRect().height ?? 0;
      const line = headerHeight + countdown + Math.min(150, window.innerHeight * .18);
      let current = '';
      this.chapters.forEach(chapter => {
        if (chapter.getBoundingClientRect().top <= line) current = chapter.id;
      });
      const lastChapter = this.chapters[this.chapters.length - 1];
      const lastBottom = lastChapter?.getBoundingClientRect().bottom;
      const withinChapters = !lastChapter || lastBottom > headerHeight + countdown + 32;
      if (!withinChapters) current = '';
      // Keep the desktop rail inside the numbered shopping sections.
      if (window.innerWidth >= 1800 && lastChapter) {
        const railBottom = window.innerHeight / 2 + this.offsetHeight / 2;
        const endOffset = Math.min(0, lastBottom - railBottom - 32);
        const railTop = window.innerHeight / 2 - this.offsetHeight / 2 + endOffset;
        this.style.setProperty('--rail-end-offset', `${endOffset}px`);
        this.style.setProperty('--rail-clip-top', `${Math.max(0, headerHeight + countdown - railTop)}px`);
      } else {
        this.style.removeProperty('--rail-end-offset');
        this.style.removeProperty('--rail-clip-top');
      }
      this.links.forEach(link => {
        if (link.hash === `#${current}`) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
      const darkSurface = current === 'bundles';
      const surface = darkSurface ? 'dark' : 'light';
      if (this.dataset.surface !== surface) this.dataset.surface = surface;
      const pastHero = Boolean(this.hero && this.hero.getBoundingClientRect().bottom <= headerHeight + countdown);
      this.classList.toggle('is-past-hero', pastHero && withinChapters);
      this.hint?.classList.toggle('is-scrolled', window.scrollY > 40);
    }

    stopMotion() {
      this.revealObserver?.disconnect();
      this.animations?.forEach(animation => animation.cancel());
      this.animations?.clear();
    }

    teardown() {
      this.controller?.abort();
      this.injectedHint?.remove();
      this.injectedHint = null;
      this.stopMotion();
      this.oilObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.root?.classList?.remove('halloween-motion-active');
    }

    disconnectedCallback() { this.teardown(); }
  });
})();
